import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  type ShouldRevalidateFunctionArgs,
} from 'react-router';
import { lazy, Suspense } from 'react';
import { getTraceData } from '@sentry/core';
import { useT } from '@transifex/react';
import { env } from 'cloudflare:workers';

import type { Route } from './+types/root';
import './app.css';
import { vegaRequestContextMiddleware } from './middleware/vega-request';
import { NotFoundPage } from './components/not-found-page';
import { ServerErrorPage } from './components/server-error-page';
import { ShowToast, Toaster } from '~/components/ui/sonner';
import { ReducedMotionProvider } from '~/contexts/reduced-motion-context';
import { FeatureFlagProvider, useFeatureFlag } from '~/contexts/feature-flag-context';
import { getToastData } from './lib/toast-utils';
import { featureFlagsMiddleware, featureFlagContext } from './middleware/feature-flags';
import { getLocale } from './lib/languages';

const LazyDebugPanel = lazy(() =>
  import('~/components/ui/feature-flag-debug-panel').then(m => ({
    default: m.DebugPanel,
  }))
);

// Preconnect to Google Fonts for faster font loading
// Font stylesheets are loaded non-blocking in Layout component
export const links: Route.LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
];

// Font URLs
const ENGLISH_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400..700;1,400..700&family=Volkhov:ital,wght@0,400;0,700;1,400;1,700&display=swap';
const JAPANESE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Zen+Old+Mincho:wght@400;900&family=IBM+Plex+Sans+JP:wght@400;600;700&display=swap';

/**
 * Non-blocking font loader script.
 * Injects link elements with media="print" and onload handler to switch to media="all".
 * This approach is needed because React doesn't support string onload handlers on link elements.
 */
function NonBlockingFontLoader({ urls }: { urls: string[] }) {
  const script = urls
    .map(
      url => `
      (function() {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '${url}';
        link.media = 'print';
        link.onload = function() { this.media = 'all'; };
        document.head.appendChild(link);
      })();
    `
    )
    .join('');

  return <script dangerouslySetInnerHTML={{ __html: script }} />;
}

/**
 * Injects Sentry trace meta tags into <head> via React instead of Sentry's
 * injectTraceMetaTags stream transformer. The Cloudflare implementation of
 * injectTraceMetaTags uses TextDecoder without { stream: true }, which corrupts
 * multi-byte UTF-8 characters (e.g. Japanese) split across chunk boundaries.
 */
function SentryTraceMetaTags() {
  const traceData = getTraceData();
  if (!traceData) return null;
  return (
    <>
      {Object.entries(traceData).map(([key, value]) => (
        <meta key={key} name={key} content={value} />
      ))}
    </>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const t = useT();
  const data = useLoaderData<typeof loader>();
  const isJapanese = data?.contentLang === 'ja';

  return (
    // When a programmatic scroll is triggered by clicking an anchor link, the page will scroll making sure the content is not covered by the sticky navbar.
    // spacing(25) accounts for the height of the navbar and crumbs, and the padding of the page container.
    <html lang={data?.contentLang || 'en'} dir="ltr" className="scroll-pt-24">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="format-detection" content="email=no, telephone=no" />
        <meta name="description" content={t('Self-learning platform for everyone')} />
        <link rel="manifest" href="/manifest.webmanifest" />
        {/* Non-blocking font loading - English fonts for all, Japanese only when needed */}
        <NonBlockingFontLoader
          urls={isJapanese ? [ENGLISH_FONTS_URL, JAPANESE_FONTS_URL] : [ENGLISH_FONTS_URL]}
        />
        <SentryTraceMetaTags />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.PUBLIC_ENV = ${JSON.stringify(data?.PUBLIC_ENV || { envName: 'unknown', affineOrigin: '' })};`,
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}

export const middleware = [vegaRequestContextMiddleware, featureFlagsMiddleware];

export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  if (args.formAction?.includes('/autosave')) {
    return false;
  }

  return args.defaultShouldRevalidate;
}

export async function loader({ request, context }: Route.LoaderArgs) {
  const { toast, headers } = await getToastData(request);

  const contentLang = await getLocale(request);

  const envName = env.ENVIRONMENT;
  const affineOrigin = env.AFFINE_ORIGIN;

  // Get feature flags from context (set by middleware)
  const featureFlags = context.get(featureFlagContext) || {};

  return data(
    {
      toast: toast ? { ...toast, ts: new Date().getTime() } : null,
      PUBLIC_ENV: { envName, affineOrigin },
      featureFlags,
      contentLang: contentLang || 'en',
    },
    { headers }
  );
}

export default function App() {
  const data = useLoaderData<typeof loader>();
  const { featureFlags } = data;

  return (
    <ReducedMotionProvider>
      <FeatureFlagProvider flags={featureFlags}>
        <Toaster />
        {data.toast ? <ShowToast toast={data.toast} /> : null}
        <DebugPanelLoader />
        <Outlet />
      </FeatureFlagProvider>
    </ReducedMotionProvider>
  );
}

function DebugPanelLoader() {
  const debugPanelEnabled = useFeatureFlag('debug-panel');
  if (!debugPanelEnabled) return null;

  return (
    <Suspense>
      <LazyDebugPanel />
    </Suspense>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <NotFoundPage />;
    }

    if (error.status >= 500) {
      return <ServerErrorPage error={error} />;
    }
  }

  // For non-route errors that are 500-level (general server errors)
  if (error instanceof Error) {
    return <ServerErrorPage error={error} />;
  }

  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = 'Error';
    details = error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="container mx-auto p-4 pt-16">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full overflow-x-auto p-4">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
